/**
 * Audit API client for admin audit log features
 */

import { api } from '../lib/api';
import type {
  AuditLogEntry,
  AuditLogFilterParams,
  Pagination,
  EventTypeInfo,
  SecurityMetrics,
} from '@leal-crm/shared';

/**
 * Audit log list response data
 */
interface AuditLogListData {
  logs: AuditLogEntry[];
  pagination: Pagination;
}

/**
 * Entity audit log response data
 */
interface EntityAuditLogData {
  logs: AuditLogEntry[];
  total: number;
}

/**
 * Convert filter params to URL query string
 */
function buildQueryString(params: AuditLogFilterParams): string {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) {
    searchParams.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    searchParams.set('pageSize', String(params.pageSize));
  }
  if (params.startDate) {
    searchParams.set('startDate', params.startDate);
  }
  if (params.endDate) {
    searchParams.set('endDate', params.endDate);
  }
  if (params.eventTypes && params.eventTypes.length > 0) {
    searchParams.set('eventTypes', params.eventTypes.join(','));
  }
  if (params.search) {
    searchParams.set('search', params.search);
  }
  if (params.entityType) {
    searchParams.set('entityType', params.entityType);
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

/**
 * Audit API client
 */
export const auditApi = {
  /**
   * Get paginated list of audit logs with optional filters
   */
  getLogs: (params: AuditLogFilterParams = {}): Promise<AuditLogListData> =>
    api.get<AuditLogListData>(`/api/admin/audit/logs${buildQueryString(params)}`),

  /**
   * Get a single audit log entry by ID
   */
  getLogById: (id: string): Promise<AuditLogEntry> =>
    api.get<AuditLogEntry>(`/api/admin/audit/logs/${id}`),

  /**
   * Get list of event types for filter dropdown
   */
  getEventTypes: (): Promise<{ eventTypes: EventTypeInfo[] }> =>
    api.get<{ eventTypes: EventTypeInfo[] }>('/api/admin/audit/event-types'),

  /**
   * Get security metrics for dashboard
   */
  getMetrics: (): Promise<SecurityMetrics> =>
    api.get<SecurityMetrics>('/api/admin/audit/metrics'),

  /**
   * Get audit logs for a specific entity (customer, vehicle, etc.)
   */
  getEntityLogs: (
    entityType: string,
    entityId: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<EntityAuditLogData> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return api.get<EntityAuditLogData>(
      `/api/admin/audit/entity/${entityType}/${entityId}${query ? `?${query}` : ''}`
    );
  },
};

export type { AuditLogListData, EntityAuditLogData };
